import React, { useState, useMemo } from 'react';
import { Box, Checkbox, Radio, Typography } from '@mui/material';
import { ChevronRightIcon, ChevronDownIcon, FolderIcon, DocumentIcon } from '@heroicons/react/24/outline';
import styles from '../../styles/query/ModuleSelector.module.scss';

/**
 * ModuleSelector Component
 * 
 * Tree-view module selector for filter dropdowns
 * Supports single-select (radio) for "=" operator and multi-select (checkbox) for "in" operator
 * 
 * @param {Object} props
 * @param {Array} props.options - Flat list of modules from schema [{id, name, path, label, level}]
 * @param {Array} props.selectedValues - Currently selected module objects
 * @param {function} props.onSelectionChange - Handler for selection change (receives array of module objects)
 * @param {string} props.operator - Current operator ('=', '!=', 'in', 'notIn')
 */
export function ModuleSelector({ 
  options = [], 
  selectedValues = [], 
  onSelectionChange, 
  operator = '=' 
}) {
  const [expanded, setExpanded] = useState([]);
  
  // Determine if multi-select is allowed based on operator
  const allowMultiSelect = useMemo(() => {
    return operator === 'in' || operator === 'notIn';
  }, [operator]);

  // Build tree structure from flat module list
  // Modules have {id, name, path, label, level} where path is like "Auth > Login > 2FA"
  const moduleTree = useMemo(() => {
    if (!Array.isArray(options) || options.length === 0) {
      return [];
    }

    // Create maps for quick lookup
    const pathToModuleMap = new Map();
    const idToModuleMap = new Map();
    
    // First pass: create all nodes
    options.forEach(module => {
      const node = { ...module, children: [] };
      // Store by both path and name (in case path is just the name)
      if (module.path) {
        pathToModuleMap.set(module.path, node);
      }
      pathToModuleMap.set(module.name, node);
      idToModuleMap.set(module.id, node);
    });

    // Second pass: build tree by matching parent paths
    const rootNodes = [];
    const processedNodes = new Set();
    
    // Process nodes in order of level (root first, then children)
    const sortedOptions = [...options].sort((a, b) => (a.level || 0) - (b.level || 0));
    
    sortedOptions.forEach(module => {
      if (processedNodes.has(module.id)) return;
      
      const node = idToModuleMap.get(module.id);
      const pathParts = module.path ? module.path.split(' > ') : [module.name];
      
      if (pathParts.length > 1) {
        // Has parent: find parent by matching parent path
        const parentPath = pathParts.slice(0, -1).join(' > ');
        const parent = pathToModuleMap.get(parentPath);
        
        if (parent && parent.id !== node.id) {
          // Avoid self-reference
          parent.children.push(node);
        } else {
          // Parent not found in list, treat as root
          rootNodes.push(node);
        }
      } else {
        // Root level module (path is just the module name or level === 1)
        rootNodes.push(node);
      }
      
      processedNodes.add(module.id);
    });

    // Sort tree nodes by name
    const sortTree = (nodes) => {
      nodes.sort((a, b) => a.name.localeCompare(b.name));
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          sortTree(node.children);
        }
      });
    };

    sortTree(rootNodes);
    return rootNodes;
  }, [options]);

  // Check if a module is selected
  const isModuleSelected = (moduleId) => {
    return selectedValues.some(selected => {
      const selectedId = typeof selected === 'object' && selected !== null ? selected.id : selected;
      return String(selectedId) === String(moduleId);
    });
  };

  // Handle module selection
  const handleModuleToggle = (module) => {
    const isSelected = isModuleSelected(module.id);
    let newSelection;

    if (isSelected) {
      // Deselect: remove from selection
      newSelection = selectedValues.filter(selected => {
        const selectedId = typeof selected === 'object' && selected !== null ? selected.id : selected;
        return String(selectedId) !== String(module.id);
      });
    } else {
      // Select: add to selection
      if (allowMultiSelect) {
        // Multi-select: add to existing selection
        newSelection = [...selectedValues, module];
      } else {
        // Single-select: replace entire selection with just this module
        newSelection = [module];
      }
    }

    onSelectionChange(newSelection);
  };

  // Toggle node expansion
  const handleNodeToggle = (nodeId) => {
    setExpanded(prev => {
      if (prev.includes(nodeId)) {
        return prev.filter(id => id !== nodeId);
      } else {
        return [...prev, nodeId];
      }
    });
  };

  // Render tree node recursively
  const renderTreeNode = (node, level = 1) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded.includes(node.id);
    const isSelected = isModuleSelected(node.id);
    const isLeaf = !hasChildren;

    return (
      <Box key={node.id} className={styles.treeNode}>
        <Box
          className={`${styles.nodeRow} ${isSelected ? styles.selected : ''}`}
          onClick={(e) => {
            // Don't toggle if clicking on expand/collapse icon or checkbox
            if (e.target.closest('[data-expand-icon]') || 
                e.target.closest('input[type="checkbox"]')) {
              return;
            }
            handleModuleToggle(node);
          }}
        >
          {/* Expand/Collapse Icon */}
          <Box
            data-expand-icon
            className={styles.expandIcon}
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) {
                handleNodeToggle(node.id);
              }
            }}
            sx={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasChildren ? 'pointer' : 'default',
              marginRight: 0.5,
            }}
          >
            {hasChildren ? (
              isExpanded ? (
                <ChevronDownIcon className="icon-small" style={{ color: '#64748b' }} />
              ) : (
                <ChevronRightIcon className="icon-small" style={{ color: '#64748b' }} />
              )
            ) : (
              <Box sx={{ width: 16, height: 16 }} />
            )}
          </Box>

          {/* Selection Control (Checkbox only for multi-select) */}
          {allowMultiSelect && (
            <Box sx={{ marginRight: 1, display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={isSelected}
                onChange={() => handleModuleToggle(node)}
                size="small"
                sx={{ padding: 0.5 }}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
          )}

          {/* Module Icon */}
          <Box
            sx={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 0.75,
            }}
          >
            {isLeaf ? (
              <DocumentIcon className="icon-small" style={{ color: '#64748b' }} />
            ) : (
              <FolderIcon className="icon-small" style={{ color: '#64748b' }} />
            )}
          </Box>

          {/* Module Name */}
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              color: isSelected ? '#1e3a8a' : '#374151',
              fontWeight: isSelected ? 500 : 400,
              fontSize: '0.875rem',
            }}
          >
            {node.name}
          </Typography>
        </Box>

        {/* Children */}
        {hasChildren && isExpanded && (
          <Box className={styles.childrenContainer} sx={{ marginLeft: level * 1.5 }}>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  if (moduleTree.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <Typography variant="body2" color="text.secondary">
          No modules available
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.moduleSelectorContainer}>
      {moduleTree.map(node => renderTreeNode(node))}
    </Box>
  );
}

export default ModuleSelector;

